import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { filter, firstValueFrom, fromEvent, Observable, Subscription } from 'rxjs';
import * as _ from 'lodash';
import Swal from 'sweetalert2';
import { NavigationEnd, Router } from '@angular/router';
import { SystemLevelService } from '../../services/system-level.service';
import { GlobalSpinnerService } from 'src/app/shared/services/global-spinner.service';
import { AuthService } from '../../services/auth.service';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import { Utils } from 'src/app/core/services/utils';
import { ITimer, TimerResult } from '../../../utils/timer';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';
import {
  CloseMessage,
  InitMessage,
  PauseMessage,
  PlayMessage,
  Tick$Message,
  Timeout$Message,
  TimerWorkerMessage,
  TimerWorkerMessageType,
} from '../../shared/workers/timer.worker';
import { SessionTimeoutStorage } from './session-timeout-sync/session-timeout-sync.component';

// How many seconds before the timeout should the warning be displayed.
// This value must be less than the timeout. Otherwise, the warning
// might not be displayed.
const _secondsBeforeTimeoutWarning = 20;
// Throttle interval for action events (in milliseconds).
// This prevents excessive timer resets from high-frequency events like mousemove/scroll.
const _actionEventThrottleMs = 1000;
const _actionEvents = Object.freeze([
  'mousemove',
  'click',
  'mouseup',
  'mousedown',
  'keydown',
  'keypress',
  'keyup',
  'submit',
  'change',
  'mouseenter',
  'scroll',
  'resize',
  'dblclick',
]);
const _excludedRoutes = Object.freeze([
  'login',
  'register',
  'reset-password',
  'forgot-password',
  'email-preference-center',
  'feewise-wizard-complete',
  'sl',
  'ext-auth-callback',
  'saml-callback',
  'tpo/esign-via-token',
  'admin/esign-via-token',
  'admin/esign-confirmation',
  'tpo/esign-confirmation',
  'message-report-msg-preview',
  'tv-auth',
  'epc-partner/document-delivery'
]);
const _swalIdentifierClass = 'session-timeout-swal';
// How long before the absolute session deadline the max-duration warning is shown, in seconds.
const _maxSessionWarningSecs = 5 * 60;
// setTimeout accepts a 32-bit millisecond delay; longer waits are re-armed in chunks.
const _maxTimeoutDelayMs = 2 ** 31 - 1;

@Component({
    selector: 'session-timeout',
    template: `
    @if (isSynced) {
      <session-timeout-sync
        [timer]='timer'
        >
      </session-timeout-sync>
    }
    `,
    standalone: false
})
export class SessionTimeoutComponent implements OnInit, OnDestroy {
  private _timer: ITimer;
  protected get timer(): ITimer {
    return this._timer;
  }

  private _warningDuration: number | undefined;

  private _warningSubscription: Subscription | undefined;
  private _timeoutSubscription: Subscription | undefined;
  private _play$Subscription: Subscription | undefined;
  private _handleActionEvent: () => void;
  private _teardownTimer: (() => void) | undefined;

  private _isRouteExcluded: boolean = true;
  private _routerSubscription: Subscription;
  private _configurationSaveSubscription: Subscription;
  private _recentTabTimeoutSubscription: Subscription;

  // Cached warning state to avoid repeated DOM queries on every action event
  private _isWarningOpen: boolean = false;
  // Timestamp of last action event handling to implement throttling
  private _lastActionEventTime: number = 0;

  protected get isSynced(): boolean {
    return this._timer != null && !this._isRouteExcluded;
  }

  // Absolute session-deadline (max duration) tracking. Independent of the per-company idle timer above:
  // it fires even when idle timeout is disabled, warning the user 5 minutes before the hard 12-hour cap.
  private _maxSessionWarningTimer: ReturnType<typeof setTimeout> | undefined;
  private _maxSessionLogoutTimer: ReturnType<typeof setTimeout> | undefined;
  private _maxSessionCountdownInterval: ReturnType<typeof setInterval> | undefined;
  private _maxSessionDeadlineMs: number | undefined;
  // Cached open-state for the max-session warning so dismissal doesn't have to query the DOM
  // (mirrors the idle warning's _isWarningOpen flag).
  private _isMaxSessionWarningOpen: boolean = false;

  constructor(
    private readonly _router: Router,
    private readonly _systemLevelService: SystemLevelService,
    private readonly _spinnerService: GlobalSpinnerService,
    private readonly _authService: AuthService,
    private readonly _ngZone: NgZone,
    private readonly _localStorage: LocalStorageService,
  ) {
  }

  ngOnInit(): Promise<void> {
    this._routerSubscription = this.subscribeToRouter();
    this._configurationSaveSubscription = this.subscribeToConfigurationSave();

    if (!this._isRouteExcluded) {
      this.initMaxSessionWarning();
      return this.initTimer();
    }
  }

  ngOnDestroy(): void {
    this._configurationSaveSubscription?.unsubscribe();
    this._routerSubscription?.unsubscribe();
    this._recentTabTimeoutSubscription?.unsubscribe();
    this.destroyTimer();
    this.clearMaxSessionTimers();
  }

  private get _isDisplayingWarning(): boolean {
    if (!Swal.isVisible()) {
      return false;
    }

    return document.querySelector(
      `.swal2-container.${_swalIdentifierClass}`,
    ) != null;
  }

  private async logout(): Promise<void> {
    const spinnerService = this._spinnerService;
    const authService = this._authService;

    await spinnerService.show();

    await firstValueFrom(authService.logout());

    await spinnerService.hide();
  }

  private async initTimer(): Promise<void> {
    const sessionTimeout = await firstValueFrom(
      this._systemLevelService.getSystemLevelSessionTimeout(),
    );
    const timeout = sessionTimeout.value;
    if (timeout == null || timeout <= 0) {
      return; 
    }

    console.assert(
      _.isInteger(timeout),
      '[sessionTimeout] must be an integer value in minutes.',
    );

    const timeoutSecs = timeout * 60;

    /**
     * Using a variable in the Worker constructor is not supported by webpack.
     * For example, the following code will not work:
     * @example
     * const url = new URL('./path/to/worker.ts', import.meta.url);
     * const worker = new Worker(url);
     *
     * @see https://webpack.js.org/guides/web-workers/
     */
    const worker = new Worker(new URL(
      '../../shared/workers/timer.worker',
      import.meta.url,
    ));
    this._timer = new TimerWorkerAdapter(worker, timeoutSecs, this._ngZone);
    this._warningDuration = timeoutSecs - _secondsBeforeTimeoutWarning;

    this._warningSubscription = this.subscribeToWarning();
    this._timeoutSubscription = this.subscribeToTimeout();
    this._play$Subscription = this.subscribeToPlay$();
    this._teardownTimer = () => worker.terminate();

    this.addHandleActionEventListeners();

    if (!this._isRouteExcluded) {
      this._timer.play();
    }
  }

  private subscribeToWarning(): Subscription {
    return this._timer.tick$.pipe(
      map((value) => value >= this._warningDuration),
      distinctUntilChanged(),
      filter((shouldDisplay) => shouldDisplay),
    ).subscribe(() => {
      // Set cached warning state to avoid DOM queries in action event handler
      this._isWarningOpen = true;

      let timeoutSubscription: Subscription;
      let tickSubscription: Subscription;
      Swal.fire({
        html: `
          Your session will expire in <b>${this._timer.remaining}</b> seconds.
          Would you like to continue your session?`,
        customClass: {
          container: _swalIdentifierClass,
        },
        timerProgressBar: true,
        didOpen: () => {
          const b = Swal.getHtmlContainer().querySelector('b');

          const timer = this._timer;
          timeoutSubscription = timer.subscribe(() => {
            this.dismissWarning(Swal.DismissReason.timer);
          });

          tickSubscription = timer.tick$.subscribe(() => {
            const elapsed = this.getSessionTimeoutStateElapsed();
            if (elapsed != null && elapsed < this._warningDuration) {
              this.dismissWarning(Swal.DismissReason.timer);
              this._timer.play(elapsed);
              return;
            }

            if (b != null) {
              b.textContent = timer.remaining.toString();
            }
          });

          // Swal's timer is not used to avoid conflicts between Swal's timer
          // and the timer. Therefore, the progress bar is simulated here.
          const progressBar = Swal.getTimerProgressBar();
          progressBar.style.cssText =
            `display: flex;
             transition: width ${timer.remaining}s linear 0s;
             width: 100%;`;
          setTimeout(() => progressBar.style.width = '0');
        },
        icon: 'warning',
        confirmButtonText: 'Yes',
      }).then((result) => {
        // Clear cached warning state when dialog closes
        this._isWarningOpen = false;

        timeoutSubscription.unsubscribe();
        tickSubscription.unsubscribe();

        if (result.dismiss !== Swal.DismissReason.timer) {
          this._timer.play(0);
        }
      });
    });
  }

  private subscribeToTimeout(): Subscription {
    return this._timer.pipe(
      filter(({ isCancelled }) => !isCancelled),
    ).subscribe(() => {
      const elapsed = this.getSessionTimeoutStateElapsed();
      if (!this._localStorage.authorizationData?.token) {
        return;
      }
      if (elapsed != null && elapsed < this._timer.duration) {
        this._timer.play(elapsed);
        return;
      }

      this.logout();
    });
  }

  private subscribeToPlay$(): Subscription {
    return this._timer.play$.pipe(
      filter((from) => from < this._warningDuration),
    ).subscribe(() => this.dismissWarning());
  }

  private addHandleActionEventListeners(): void {
    const handle = this._handleActionEvent = () => {
      // Do not reset the timer if the warning is open, ignore the events.
      // Use cached state to avoid expensive DOM queries on every event.
      if (this._isWarningOpen) {
        return;
      }

      // Throttle timer resets to avoid excessive worker messages from
      // high-frequency events like mousemove, scroll, resize.
      const now = Date.now();
      if (now - this._lastActionEventTime < _actionEventThrottleMs) {
        return;
      }
      this._lastActionEventTime = now;

      this._timer?.play(0);
    };
    // Add listeners outside Angular zone to prevent change detection on every scroll/mousemove
    this._ngZone.runOutsideAngular(() => {
      _actionEvents.forEach((e) => document.addEventListener(e, handle));
    });
  }

  private dismissWarning(reason?: typeof Swal.DismissReason[keyof typeof Swal.DismissReason]) {
    if (this._isDisplayingWarning) {
      Swal.close({
        isDismissed: true,
        dismiss: reason ?? Swal.DismissReason.cancel,
      });
    }
  }

  private subscribeToRouter(): Subscription {
    return this._router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
    ).subscribe((e: NavigationEnd) => {
      const isRouteExcluded = _excludedRoutes.some((p) => e.url.startsWith(`/${p}`));
      this._isRouteExcluded = isRouteExcluded;

      if (isRouteExcluded) {
        this.destroyTimer();
        this.clearMaxSessionTimers();
      } else {
        this.initMaxSessionWarning();
        if (this._timer == null) {
          return this.initTimer();
        }
      }
    });
  }

  private subscribeToConfigurationSave(): Subscription {
    return this._systemLevelService
      .configurationSave.pipe(
        filter(({ key }) => key === 'GetSystemTimeout'),
      ).subscribe(() => {
        this.dismissWarning();

        return this.resetTimer();
      });
  }

  private destroyTimer(): void {
    this._warningSubscription?.unsubscribe();
    this._timeoutSubscription?.unsubscribe();
    this._play$Subscription?.unsubscribe();
    this._teardownTimer?.();
    this._timer?.close();
    this._timer = null;

    // Reset cached states
    this._isWarningOpen = false;
    this._lastActionEventTime = 0;

    const handle = this._handleActionEvent;
    if (handle != null) {
      _actionEvents.forEach((e) => document.removeEventListener(e, handle));
    }

    // This might be necessary if the user logs out programmatically from
    // outside of this component, after the warning has been displayed.
    this.dismissWarning();
  }

  private resetTimer(): Promise<void> {
    this.destroyTimer();
    return this.initTimer();
  }

  /**
   * Reads the absolute session deadline from the current access token's `SessionExpires` claim and
   * schedules a one-time 5-minute warning followed by an automatic logout at the hard 12-hour cap. The
   * deadline is preserved by the server across token refreshes, so re-reading it is idempotent.
   */
  private initMaxSessionWarning(): void {
    const deadlineMs = this.getSessionDeadlineMs();
    if (deadlineMs == null) {
      this.clearMaxSessionTimers();
      return;
    }

    // Already scheduled against this exact deadline - nothing to do.
    if (this._maxSessionDeadlineMs === deadlineMs
      && (this._maxSessionWarningTimer != null || this._maxSessionLogoutTimer != null)) {
      return;
    }

    this.clearMaxSessionTimers();
    this._maxSessionDeadlineMs = deadlineMs;

    const warnAtMs = deadlineMs - _maxSessionWarningSecs * 1000;
    const now = Date.now();

    this._maxSessionWarningTimer = this.armTimer(
      warnAtMs - now,
      () => this.showMaxSessionWarning(),
    );
    this._maxSessionLogoutTimer = this.armTimer(
      deadlineMs - now,
      () => this.expireMaxSession(),
    );
  }

  private getSessionDeadlineMs(): number | undefined {
    const token = this._localStorage.authorizationData?.token;
    if (!token) {
      return undefined;
    }

    let payload: { SessionExpires?: number | string };
    try {
      payload = Utils.parseJwt(token);
    } catch {
      return undefined;
    }

    const sessionExpires = Number(payload?.SessionExpires);
    if (!Number.isFinite(sessionExpires) || sessionExpires <= 0) {
      return undefined;
    }

    // The claim is stored as Unix seconds.
    return sessionExpires * 1000;
  }

  /**
   * setTimeout truncates delays that exceed a signed 32-bit int, so long waits (up to ~12h) are chained in
   * chunks until the true target time is reached.
   */
  private armTimer(delayMs: number, onDue: () => void): ReturnType<typeof setTimeout> {
    const boundedDelay = Math.max(0, Math.min(delayMs, _maxTimeoutDelayMs));
    return setTimeout(() => {
      const remaining = delayMs - boundedDelay;
      if (remaining > 0) {
        return this._ngZone.run(() => this.armTimer(remaining, onDue));
      }
      this._ngZone.run(onDue);
    }, boundedDelay);
  }

  private showMaxSessionWarning(): void {
    // Re-read because another tab may have replaced the session token.
    const deadlineMs = this.getSessionDeadlineMs();
    if (deadlineMs == null) {
      this.dismissMaxSessionWarning();
      this.clearMaxSessionTimers();
      this._maxSessionDeadlineMs = undefined;
      return;
    }

    if (deadlineMs !== this._maxSessionDeadlineMs) {
      this.dismissMaxSessionWarning();
      this.rearmMaxSessionTimers();
      return;
    }

    const remainingSecs = () => Math.max(
      0,
      Math.ceil((this._maxSessionDeadlineMs - Date.now()) / 1000),
    );

    this._isMaxSessionWarningOpen = true;
    Swal.fire({
      title: 'Maximum Session Duration Reached',
      icon: 'warning',
      html: `
        You will be logged out in <b>${this.formatCountdown(remainingSecs())}</b>.
        <br /><br />
        Please save your work now. To avoid losing any changes, manually log out and log back in before the
        countdown ends.`,
      allowOutsideClick: false,
      showConfirmButton: true,
      confirmButtonText: 'OK',
      didOpen: () => {
        const b = Swal.getHtmlContainer()?.querySelector('b');
        this._ngZone.runOutsideAngular(() => {
          this._maxSessionCountdownInterval = setInterval(() => {
            const deadlineMs = this.getSessionDeadlineMs();
            if (deadlineMs == null) {
              return this._ngZone.run(() => {
                this.dismissMaxSessionWarning();
                this.clearMaxSessionTimers();
                this._maxSessionDeadlineMs = undefined;
              });
            }
            if (deadlineMs !== this._maxSessionDeadlineMs) {
              return this._ngZone.run(() => {
                this.dismissMaxSessionWarning();
                this.rearmMaxSessionTimers();
              });
            }

            if (b != null) {
              b.textContent = this.formatCountdown(remainingSecs());
            }
          }, 1000);
        });
      },
      willClose: () => {
        this._isMaxSessionWarningOpen = false;
        this.clearMaxSessionCountdownInterval();
      },
    });
  }

  private formatCountdown(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  private expireMaxSession(): void {
    const deadlineMs = this.getSessionDeadlineMs();
    if (deadlineMs == null || !this._localStorage.authorizationData?.token) {
      this.clearMaxSessionTimers();
      this._maxSessionDeadlineMs = undefined;
      return;
    }

    if (deadlineMs !== this._maxSessionDeadlineMs || Date.now() < deadlineMs) {
      this.rearmMaxSessionTimers();
      return;
    }

    this.clearMaxSessionTimers();
    this.dismissMaxSessionWarning();
    // logout() clears auth/localStorage and navigates to the login page (and expires the server-side session).
    this._authService.logout();
  }

  private getSessionTimeoutStateElapsed(): number | undefined {
    try {
      const value = localStorage.getItem(SessionTimeoutStorage.storageKey);
      const elapsed = value != null ? JSON.parse(value)?.elapsed : undefined;
      return Number.isSafeInteger(elapsed) && elapsed >= 0 ? elapsed : undefined;
    } catch {
      return undefined;
    }
  }

  private rearmMaxSessionTimers(): void {
    this.clearMaxSessionTimers();
    this._maxSessionDeadlineMs = undefined;
    this.initMaxSessionWarning();
  }

  private clearMaxSessionTimers(): void {
    if (this._maxSessionWarningTimer != null) {
      clearTimeout(this._maxSessionWarningTimer);
      this._maxSessionWarningTimer = undefined;
    }
    if (this._maxSessionLogoutTimer != null) {
      clearTimeout(this._maxSessionLogoutTimer);
      this._maxSessionLogoutTimer = undefined;
    }
    this.clearMaxSessionCountdownInterval();
    this._maxSessionDeadlineMs = undefined;
    this.dismissMaxSessionWarning();
  }

  private clearMaxSessionCountdownInterval(): void {
    if (this._maxSessionCountdownInterval != null) {
      clearInterval(this._maxSessionCountdownInterval);
      this._maxSessionCountdownInterval = undefined;
    }
  }

  private dismissMaxSessionWarning(): void {
    if (this._isMaxSessionWarningOpen) {
      Swal.close();
    }
  }
}

export class TimerWorkerAdapter extends Observable<TimerResult> implements ITimer {
  public readonly tick$: Observable<number>;
  public readonly play$: Observable<number>;

  private _result: TimerResult | undefined;

  get isExpired(): boolean {
    return this._result != null;
  }

  private _elapsed: number = 0;
  get elapsed(): number {
    return this._elapsed;
  }

  get remaining(): number {
    return this.duration - this._elapsed;
  }

  constructor(
    private readonly _worker: Worker,
    readonly duration: number,
    ngZone: NgZone,
  ) {
    // Listen to the worker outside the Angular zone so its once-a-second ticks don't run change detection on
    // every page. fromEvent adds its listener when it is subscribed to, not when it is created, so the
    // subscription itself has to happen outside the zone; every consumer subscribes from inside it.
    const message$ = new Observable<TimerWorkerMessage>((subscriber) =>
      ngZone.runOutsideAngular(() =>
        fromEvent<MessageEvent<TimerWorkerMessage>>(_worker, 'message')
          .pipe(map(({ data }) => data))
          .subscribe(subscriber),
      ),
    );

    super((subscriber) => {
      const subscription = message$.pipe(
        filter(({ type }) => type === TimerWorkerMessageType.timeout$),
        map((message: Timeout$Message) => message.result),
        tap((value) => this._result = value),
      ).subscribe({
        // The timeout logs the user out (spinner, HTTP, navigation), which has to run inside the zone.
        next: (value) => ngZone.run(() => subscriber.next(value)),
        error: (err) => subscriber.error(err),
        complete: () => subscriber.complete(),
      });

      return () => {
        this.pause();
        subscription.unsubscribe();
      };
    });

    this.postMessage(new InitMessage(duration));

    this.tick$ = message$.pipe(
      filter(({ type }) => type === TimerWorkerMessageType.tick$),
      map((message: Tick$Message) => message.elapsed),
      tap((value) => this._elapsed = value),
    );

    this.play$ = message$.pipe(
      filter(({ type }) => type === TimerWorkerMessageType.play$),
      map((message: PlayMessage) => message.from),
      tap((value) => this._elapsed = value),
    );
  }

  private postMessage(message: TimerWorkerMessage) {
    this._worker.postMessage(message);
  }

  play(from?: number): void {
    this.postMessage(new PlayMessage(from));
  }

  pause(): void {
    this.postMessage(new PauseMessage());
  }

  close(): void {
    this.postMessage(new CloseMessage());
  }
}
